import { Filter } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import {
  SPECIFIC_CHARACTER_FILTER_ROLES,
  SpecificCharacterFilters,
} from './useSpecificCharacterFilters'

type TeamRankOption = {
  id: number
  name: string
}

type Props = {
  filters: SpecificCharacterFilters
  onFiltersChange: (
    updater: (prev: SpecificCharacterFilters) => SpecificCharacterFilters,
  ) => void
  availableTeamRanks: TeamRankOption[]
  defaultEnabledTeamRankIds: number[]
  enabledTeamRankIdSet: Set<number>
  hasOverrides: boolean
}

const SpecificCharacterFilterMenu = ({
  filters,
  onFiltersChange,
  availableTeamRanks,
  defaultEnabledTeamRankIds,
  enabledTeamRankIdSet,
  hasOverrides,
}: Props) => {
  const { t } = useTranslation()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type='button'
          variant='ghost'
          size='icon'
          className={cn('h-4 w-4', hasOverrides && 'text-primary')}
        >
          <Filter className='h-3.5 w-3.5' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='start'>
        <DropdownMenuLabel>
          {t(
            'dashboard.team.plans.assignments.categories.specific-character-filter-title',
          )}
        </DropdownMenuLabel>
        {SPECIFIC_CHARACTER_FILTER_ROLES.map((role) => (
          <DropdownMenuCheckboxItem
            key={role}
            checked={filters.roles[role]}
            onCheckedChange={(checked) =>
              onFiltersChange((prev) => ({
                ...prev,
                roles: {
                  ...prev.roles,
                  [role]: checked === true,
                },
              }))
            }
            onSelect={(event) => event.preventDefault()}
          >
            <span className='inline-flex items-center gap-1.5'>
              <CharacterRoleIcon
                role={role}
                size='small'
                className='shrink-0'
              />
              <span>
                {t(`dashboard.team.plans.assignments.entities.roles.${role}`)}
              </span>
            </span>
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>
          {t(
            'dashboard.team.plans.assignments.categories.specific-character-filter-ranks',
          )}
        </DropdownMenuLabel>
        {availableTeamRanks.map((rank) => (
          <DropdownMenuCheckboxItem
            key={rank.id}
            checked={enabledTeamRankIdSet.has(rank.id)}
            onCheckedChange={(checked) =>
              onFiltersChange((prev) => {
                const next = new Set(
                  prev.enabledTeamRankIds ?? defaultEnabledTeamRankIds,
                )
                if (checked === true) {
                  next.add(rank.id)
                } else {
                  next.delete(rank.id)
                }
                return {
                  ...prev,
                  enabledTeamRankIds: [...next],
                }
              })
            }
            onSelect={(event) => event.preventDefault()}
          >
            {rank.name}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default SpecificCharacterFilterMenu
